import { saveAddressFromFields } from "./address";
import { parseLocalizedNumber } from "./number-parse";
import { normalizeZip } from "./address";
import { STORAGE_KEYS, writeStorage } from "./storage";

const RENT_ALIASES = [
  "e:information.apartment_rent_amount:number",
  "information.apartment_rent_amount",
  "apartment_rent_amount",
  "apartmentrentamount",
  "current_rent",
  "currentrent",
  "rent",
  "monthly_rent",
  "monthlyrent",
];

const AREA_ALIASES = [
  "e:information.generic.rent.apartment_living_area:number",
  "information.generic.rent.apartment_living_area",
  "apartment_living_area",
  "apartmentlivingarea",
  "living_area",
  "livingarea",
  "apartment_size",
  "apartmentsize",
  "area",
  "sqm",
];

const ZIP_ALIASES = [
  "conny_zip_code",
  "conny_address_zip",
  "zip",
  "postal_code",
  "postalcode",
  "plz",
  "address_zip",
];

const CITY_ALIASES = [
  "conny_address_city",
  "city",
  "stadt",
  "address_city",
  "town",
  "ort",
];

function normalizeId(v: string): string {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/^e:/, "")
    .replace(/:number$/, "")
    .replace(/:text$/, "")
    .replace(/:string$/, "")
    .replace(/[^a-z0-9_.]/g, "");
}
function lastPart(v: string): string {
  const c = normalizeId(v);
  const p = c.split(".");
  return p[p.length - 1] || c;
}
function variableMatches(name: string, aliases: readonly string[]): boolean {
  const n = normalizeId(name);
  if (!n) return false;
  return aliases.some((a) => {
    const na = normalizeId(a);
    const la = lastPart(a);
    return n === na || n === la || n.endsWith("." + la) || na.endsWith("." + n);
  });
}

function readHeyflowFields(detail: unknown): Record<string, unknown> {
  if (!detail || typeof detail !== "object") return {};
  const d = detail as Record<string, unknown>;
  if (d.fieldsSimple && typeof d.fieldsSimple === "object")
    return d.fieldsSimple as Record<string, unknown>;
  return {};
}

function handleEvent(event: Event): void {
  captureFlowAPI(event);
  const detail = (event as CustomEvent).detail;
  const fields = readHeyflowFields(detail);
  if (!Object.keys(fields).length) return;

  saveAddressFromFields(fields);

  for (const [k, v] of Object.entries(fields)) {
    if (v == null || String(v).trim() === "") continue;
    if (variableMatches(k, RENT_ALIASES)) {
      const r = parseLocalizedNumber(v);
      if (r && r > 0) writeStorage(STORAGE_KEYS.rent, String(r));
    }
    if (variableMatches(k, AREA_ALIASES)) {
      const a = parseLocalizedNumber(v);
      if (a && a > 0) writeStorage(STORAGE_KEYS.area, String(a));
    }
    if (variableMatches(k, ZIP_ALIASES)) {
      const z = normalizeZip(v);
      if (z) writeStorage(STORAGE_KEYS.zip, z);
    }
    if (variableMatches(k, CITY_ALIASES)) {
      const c = String(v).trim();
      if (c) writeStorage(STORAGE_KEYS.city, c);
    }
  }
}

type FlowBlock = { variableName: string; setValue: (v: string) => void };
type FlowAPI = { v1: { getBlocks: () => FlowBlock[] } };

let flowAPI: FlowAPI | null = null;

function captureFlowAPI(event: Event): void {
  const detail = (event as CustomEvent).detail as Record<string, unknown>;
  if (!flowAPI && detail?.flowAPI) {
    flowAPI = detail.flowAPI as FlowAPI;
  }
}

export function setBlockValue(variableName: string, value: string): void {
  try {
    flowAPI?.v1
      .getBlocks()
      .find((b) => b.variableName === variableName)
      ?.setValue(value);
  } catch {
    /* ignore */
  }
}

let started = false;

/** Subscribe once to Heyflow's lifecycle events and mirror useful values into sessionStorage. */
export function startHeyflowBridge(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  const events = [
    "heyflow-init",
    "heyflow-change",
    "heyflow-screen-view",
    "heyflow-screen-leave",
    "heyflow-submit",
  ];
  for (const e of events)
    window.addEventListener(e, handleEvent as EventListener);
}
